import { Button } from '@emailforge/ui';
import { Component, type ErrorInfo, type ReactNode } from 'react';

import { captureException } from '@/lib/monitoring';

type Props = { children: ReactNode };
type State = { error: Error | null; eventTime: number };

/**
 * App-level boundary. Renders a friendly fallback when a render-time error
 * escapes a deeper component, ships the exception to Sentry (if initialized),
 * and lets the user reload to recover. Lives at the very top of the React
 * tree in `main.tsx` so it catches anything inside `RouterProvider`.
 *
 * Doesn't catch async errors (those need explicit `captureException` in the
 * relevant async boundary) or events fired outside React's render path.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null, eventTime: 0 };

  static getDerivedStateFromError(error: Error): State {
    return { error, eventTime: Date.now() };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    captureException(error, { componentStack: info.componentStack ?? null });
  }

  override render() {
    if (!this.state.error) return this.props.children;
    return (
      <div
        role="alert"
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--paper)',
          padding: 24,
        }}
      >
        <div style={{ maxWidth: 480, textAlign: 'center' }}>
          <div
            className="mono"
            style={{
              fontSize: 11,
              textTransform: 'uppercase',
              letterSpacing: 0.08,
              color: 'var(--flame-2)',
              marginBottom: 8,
            }}
          >
            Something broke
          </div>
          <h1 className="display" style={{ fontSize: 32, margin: '0 0 12px', letterSpacing: -0.02 }}>
            We logged the error and we're on it.
          </h1>
          <p style={{ fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.6, margin: '0 0 20px' }}>
            A page-level exception escaped the React tree. The error has been reported and you can
            reload to keep going. If the same error keeps happening, email{' '}
            <a href="mailto:support@emailforge.app" style={{ color: 'var(--flame-2)' }}>
              support@emailforge.app
            </a>
            .
          </p>
          {import.meta.env.DEV && (
            <pre
              className="mono"
              style={{
                textAlign: 'left',
                fontSize: 11,
                background: 'var(--paper-2)',
                border: '1px solid var(--line)',
                borderRadius: 'var(--r-2)',
                padding: 12,
                color: 'var(--ink-2)',
                overflow: 'auto',
                margin: '0 0 18px',
              }}
            >
              {this.state.error.stack ?? this.state.error.message}
            </pre>
          )}
          <Button variant="flame" onClick={() => {
              window.location.reload();
            }}
          >
            Reload the app
          </Button>
        </div>
      </div>
    );
  }
}
